import React, { useState, useEffect, useMemo } from 'react';

// Modal for creating a brand (parity with admin BrandModal fields)
export default function BrandModal({ open, onClose, onCreate, error, setError, creating, brandOptions = [] }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    picture: '',
    tags: [],
    tagInput: '',
    metaTitle: '',
    metaDescription: '',
    keywords: '',
    featured: false,
    status: 'active'
  });

  useEffect(() => {
    if (open) {
      setForm({ name: '', description: '', picture: '', tags: [], tagInput: '', metaTitle: '', metaDescription: '', keywords: '', featured: false, status: 'active' });
      setError && setError('');
    }
  }, [open, setError]);

  const brandSuggestions = useMemo(() => {
    const q = (form.name || '').trim().toLowerCase();
    if (!q) return [];
    return (brandOptions || [])
      .filter((b) => b?.name && b.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [form.name, brandOptions]);

  if (!open) return null;

  const uploadDirect = async (file) => {
    const fd = new FormData();
    fd.append('files', file);
    const vendorToken = localStorage.getItem('vendorToken');
    const userToken = localStorage.getItem('token');
    const bearer = vendorToken || userToken || '';
    const res = await fetch(`${import.meta.env.VITE_API_URL}/media/vendor/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${bearer}` },
      body: fd
    });
    const data = await res.json();
    if (!res.ok || !data?.data?.length) throw new Error(data?.error || 'Upload failed');
    return data.data[0].url;
  };

  const addTag = () => {
    const val = form.tagInput.trim();
    if (!val) return;
    if (form.tags.includes(val)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, val], tagInput: '' }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl bg-white dark:bg-admin-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-admin-slate-700 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-admin-slate-700 flex items-center justify-between flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add New Brand</h3>
          <button type="button" className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" onClick={onClose}>×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); if (error) setError(''); }}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white"
              />
              {form.name?.trim() && (
                <div className="mt-2 border border-gray-200 dark:border-admin-slate-600 rounded-md max-h-40 overflow-auto">
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-admin-slate-600">Similar existing brands</div>
                  {brandSuggestions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No close matches</div>
                  ) : (
                    brandSuggestions.map((b) => (
                      <button
                        key={b._id || b.value}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-admin-slate-600 dark:text-white"
                        onClick={() => setForm((f) => ({ ...f, name: b.name || b.label }))}
                      >
                        {b.name || b.label}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
              <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Brand Image</label>
              <div className="space-y-2">
                {form.picture && (
                  <div className="relative w-24 h-24 rounded-lg overflow-hidden">
                    <img src={form.picture} alt="Brand preview" className="w-full h-full object-cover" />
                    <button type="button" className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white" onClick={() => setForm((f) => ({ ...f, picture: '' }))}>×</button>
                  </div>
                )}
                <input type="file" accept="image/*" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try { const url = await uploadDirect(file); setForm((f) => ({ ...f, picture: url })); }
                  catch (err) { setError && setError(err.message || 'Failed to upload image'); }
                }} className="w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md dark:text-white" />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tags</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {form.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-admin-slate-600 text-gray-800 dark:text-gray-200">
                    {tag}
                    <button type="button" className="ml-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white" onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }))}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={form.tagInput} onChange={(e) => setForm((f) => ({ ...f, tagInput: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Enter a tag and press Enter" className="flex-1 px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white" />
                <button type="button" onClick={addTag} className="px-3 py-2 bg-primary-600 text-white rounded-md">Add</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Meta Title</label>
              <input type="text" value={form.metaTitle} onChange={(e) => setForm((f) => ({ ...f, metaTitle: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Keywords</label>
              <input type="text" value={form.keywords} onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))} placeholder="SEO keywords" className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Meta Description</label>
              <textarea value={form.metaDescription} onChange={(e) => setForm((f) => ({ ...f, metaDescription: e.target.value }))} rows={2} className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white" />
            </div>
          </div>
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-admin-slate-700 flex items-center justify-end gap-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-admin-slate-700 text-gray-700 dark:text-gray-300 rounded-md">Cancel</button>
          <button type="button" disabled={creating} onClick={() => onCreate(form)} className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50">{creating ? 'Creating...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}
